
import { ACTIVE_CHAIN } from "@/config/chain";
import { VENUES } from "@/config/contracts";
import { CADC, USDC } from "@/config/tokens";
import { formatPercent, formatToken } from "@/lib/format";
import { formatHealthFactor } from "@/lib/loop/risk";
import type { PositionSummary } from "@/hooks/use-position-summary";

type Props = {
  readonly summary: PositionSummary;
  readonly onModify: () => void;
  readonly onClose: () => void;
};

// Per-venue brand accent: a tinted hairline border plus a soft outer glow, so
// each position reads as belonging to its lending market at a glance.
const VENUE_GLOW: Record<
  Props["summary"]["venue"],
  { ring: string; glow: string }
> = {
  morpho: { ring: "rgba(36, 99, 235, 0.70)", glow: "rgba(36, 99, 235, 0.40)" },
  ajna: { ring: "rgba(124, 58, 237, 0.70)", glow: "rgba(124, 58, 237, 0.40)" },
};

type Health = { readonly label: string; readonly color: string };

const healthOf = (hf: number | undefined): Health => {
  if (hf == null || !Number.isFinite(hf)) {
    return { label: "—", color: "var(--ink-4)" };
  }
  if (hf < 1.1) return { label: "at risk", color: "var(--accent-rose)" };
  if (hf < 1.5) return { label: "moderate", color: "var(--ink-2)" };
  return { label: "healthy", color: "var(--accent-jade)" };
};

const fmtLiq = (p: number | null | undefined): string =>
  p == null ? "—" : `$${p.toFixed(4)} / ${CADC.symbol}`;

const SecondaryButton = ({
  label,
  onClick,
  tone = "ink",
}: {
  label: string;
  onClick: () => void;
  tone?: "ink" | "rose";
}) => (
  <button
    type="button"
    onClick={onClick}
    className="px-5 h-10 rounded-[var(--radius-sm)] font-mono text-[11px] tracking-[0.18em] uppercase transition-colors"
    style={{
      border: "1px solid var(--line-2)",
      color: tone === "rose" ? "var(--accent-rose)" : "var(--ink-1)",
      fontWeight: 500,
    }}
  >
    {label}
  </button>
);

export const PositionCard = ({ summary, onModify, onClose }: Props) => {
  const { venue, collateralRaw, borrowRaw, risk, leverage, borrowApr, netApr, ajnaPositionAddress } =
    summary;
  const lender = VENUES[venue];
  const health = healthOf(risk?.healthFactor);
  const accent = VENUE_GLOW[venue];

  const collateralStr =
    collateralRaw == null
      ? "—"
      : `${formatToken(collateralRaw, CADC.decimals, 2)} ${CADC.symbol}`;
  const borrowStr =
    borrowRaw == null
      ? "—"
      : `${formatToken(borrowRaw, USDC.decimals, 2)} ${USDC.symbol}`;

  const rows: ReadonlyArray<{
    label: string;
    value: string;
    hint?: string;
    tone?: string;
  }> = [
    { label: "Borrowed", value: borrowStr },
    {
      label: "Net APY",
      value: netApr == null ? "—" : formatPercent(netApr, 2),
      hint:
        borrowApr == null
          ? "combined carry"
          : `lend +0.00% · borrow −${formatPercent(borrowApr, 2)}`,
      tone:
        netApr == null
          ? undefined
          : netApr >= 0
            ? "var(--accent-jade)"
            : "var(--ink-1)",
    },
    {
      label: "Health factor",
      value: risk ? formatHealthFactor(risk.healthFactor) : "—",
      tone: health.color,
    },
    { label: "Liquidation", value: fmtLiq(risk?.liquidationPrice) },
  ];

  return (
    <article
      className="surface-strong rounded-[var(--radius-lg)] p-6 md:p-8 fade-up"
      style={{
        borderColor: accent.ring,
        borderWidth: "1.5px",
        boxShadow: `0 16px 72px -16px ${accent.glow}`,
      }}
    >
      <div className="flex items-baseline justify-between mb-5">
        <span
          className="font-mono text-[10px] tracking-[0.18em] uppercase"
          style={{ color: "var(--ink-4)" }}
        >
          Live · {lender.shortLabel}
        </span>
        <span className="inline-flex items-center gap-2">
          <span
            className="inline-block w-1.5 h-1.5 rounded-full"
            style={{ background: health.color }}
            aria-hidden
          />
          <span
            className="font-mono text-[10px] tracking-[0.16em] uppercase"
            style={{ color: health.color }}
          >
            {health.label}
          </span>
        </span>
      </div>

      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <div
            className="font-serif text-[34px] md:text-[40px]"
            style={{ fontWeight: 300, letterSpacing: "-0.01em", color: "var(--ink-1)" }}
          >
            {collateralStr}
          </div>
          <div
            className="font-mono text-[10px] tracking-[0.16em] uppercase mt-1"
            style={{ color: "var(--ink-4)" }}
          >
            Collateral{leverage == null ? "" : ` · ${leverage.toFixed(2)}× exposure`}
          </div>
        </div>
      </div>

      <ul className="space-y-3 mb-7">
        {rows.map((r, i) => (
          <li
            key={r.label}
            className="flex items-baseline justify-between gap-6 pb-3"
            style={{
              borderBottom:
                i === rows.length - 1 ? "none" : "1px dashed var(--line-1)",
            }}
          >
            <span
              className="font-sans text-[12px] tracking-[0.04em]"
              style={{ color: "var(--ink-3)" }}
            >
              {r.label}
            </span>
            <span className="text-right">
              <span
                className="font-mono tabular text-[14px]"
                style={{ color: r.tone ?? "var(--ink-1)", fontWeight: 400 }}
              >
                {r.value}
              </span>
              {r.hint && (
                <span
                  className="block font-sans text-[10px] tracking-[0.08em] uppercase mt-1"
                  style={{ color: "var(--ink-4)" }}
                >
                  {r.hint}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>

      {ajnaPositionAddress && (
        <div className="mb-5 font-mono text-[10px] tracking-[0.06em]" style={{ color: "var(--ink-4)" }}>
          <span className="uppercase tracking-[0.14em] mr-2">User Proxy</span>
          <a
            href={`${ACTIVE_CHAIN.blockExplorers?.default.url}/address/${ajnaPositionAddress}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
            style={{ color: "var(--ink-3)" }}
          >
            {ajnaPositionAddress.slice(0, 10)}…{ajnaPositionAddress.slice(-8)}
          </a>
        </div>
      )}

      <div className="flex items-center justify-end gap-3">
        <SecondaryButton label="Modify" onClick={onModify} />
        <SecondaryButton label="Close" onClick={onClose} tone="rose" />
      </div>
    </article>
  );
};
